import React, { useEffect, useState } from 'react';
import { Button, Icon, IconType, MessageBar, Dropdown } from '@repo/ui/components';
import { FinancingType, IBosCustomerFields, IOrder } from 'next-common';
import defaultTo from 'lodash/defaultTo';
import { BosDropDownOptions, TMessageBarSeverity } from 'types';

interface IAlternativeFinancing {
  leasingCompanies?: IBosCustomerFields[];
  loading?: boolean;
  isSaving?: boolean;
  order?: IOrder;
  onAlertModalToggle?(): void;
  setLeasingInput(value): void;
  onAddCalculation?(financingType: FinancingType, changes?: any): Promise<IOrder>;
  removeAlternativeLeasing?(id: string): void;
  setAddNew?(addNew?: boolean): void;
}

const getLeasingCompanyOptions = (leasingCompanies: IBosCustomerFields[], loading: boolean) => {
  if (loading) {
    return [{ label: 'Loading...', value: '' }];
  }
  return leasingCompanies?.map((organization) => {
    return {
      label: organization?.organizationName,
      value: organization?.organizationNo,
    };
  });
};

export const AlternativeFinancing = (props: IAlternativeFinancing) => {
  const order = defaultTo(props?.order, null);
  const [selectedOption, setSelectedOption] = useState(null);
  const [isChange, setIsChange] = useState(false);
  const alternativeLeasing = defaultTo(order?.alternativeLeasing, null);
  const leasingCompanyOptions = getLeasingCompanyOptions(props?.leasingCompanies, props?.loading);

  useEffect(() => {
    if (alternativeLeasing) {
      const option = { label: alternativeLeasing.organizationName, value: alternativeLeasing.organizationNo };
      setSelectedOption({ ...option, label: customOption(option) });
      setIsChange(alternativeLeasing?.organizationNo !== option?.value);
    } else {
      setSelectedOption(null);
    }
  }, [alternativeLeasing]);

  const customOption = (param: BosDropDownOptions) => (
    <div className="grid grid-cols-2 w-full gap-4">
      <span className="truncate text-base/[175%] font-semibold flex-1">{defaultTo(param?.label, '')}</span>
      <span className="truncate text-base/[175%] font-semibold text-right">{defaultTo(param?.value, '')}</span>
    </div>
  );

  const getSelectedLeasingCompany = () => {
    return props?.leasingCompanies?.find((company) => company?.organizationNo === selectedOption?.value);
  };

  const handleSave = () => {
    const allOffers = defaultTo(order?.offers, []);
    const leasingOffers = allOffers?.filter((offer) => offer?.financingType === FinancingType.LEASING);
    const leasingCompany = getSelectedLeasingCompany();
    props?.setLeasingInput({ financingType: FinancingType.ALTERNATIVELEASING, input: leasingCompany });
    if (leasingOffers?.length) {
      props?.onAlertModalToggle();
      props?.setAddNew(true);
    } else {
      props?.onAddCalculation(FinancingType.ALTERNATIVELEASING, leasingCompany);
      props?.setAddNew(false);
    }
  };

  const handleChange = (param: BosDropDownOptions) => {
    setIsChange(param?.value !== alternativeLeasing?.organizationNo);
    setSelectedOption(param);
  };

  return (
    <div className="flex items-stretch gap-4 bg-lightGrey w-full">
      <div className="w-full p-6 flex flex-col gap-6 rounded-lg border border-neutralGrey bg-white">
        <h3 className="text-lg/[170%] font-extrabold">Alternativt leasingselskap</h3>
        <MessageBar
          severity={TMessageBarSeverity.INFORMATION}
          className="w-full !max-w-none"
          alertIcon={{ icon: IconType.CircleInfo }}
        >
          <span className="text-base/[175%] font-semibold">
            Her kan du velge leasing fra et alternativt selskap.
            <br />
            Valgt selskap blir da eier av bilen.
          </span>
        </MessageBar>
        <Dropdown
          label="Velg leasingselskap"
          value={selectedOption}
          options={leasingCompanyOptions?.map((item) => ({ ...item, label: customOption(item) }))}
          placeholder="Velg leasingselskap"
          iconProps={{
            iconDropdownDown: IconType.NewNextChevronDown,
            iconDropdownUp: IconType.NewNextChevronUp,
          }}
          maxMenuHeight={300}
          nextDropdown
          onChange={(param) => {
            handleChange(param);
          }}
          menuClassName="border-0"
          hideSelectionIcon
          className="gap-4"
        />
        <div className="flex justify-between gap-6 w-full">
          <Button
            variant="teal"
            isLoading={props?.isSaving}
            onClick={() => handleSave()}
            className="w-full"
            disabled={!selectedOption || !isChange}
          >
            <span className="text-base/[175%] font-extrabold">Oppdater alternativ leasing</span>
          </Button>
          <Button
            appearance="outline"
            variant="red"
            isLoading={props?.isSaving}
            onClick={() => props?.removeAlternativeLeasing(order?.id)}
            className="w-full"
            disabled={!alternativeLeasing}
          >
            <Icon
              icon={IconType?.NewNextTrash}
              width={24}
              height={24}
              fill={!alternativeLeasing ? 'var(--color-neutralPlus)' : 'var(--color-black)'}
            />
            <span className="text-base/[175%] font-extrabold">Slett alternativ leasing</span>
          </Button>
        </div>
      </div>
    </div>
  );
};
